import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { MainLayout } from "layouts";
import { useAuthContext } from "contexts";
import { getBlockToReview, markBlockReviewed } from "api";
import { Table, Button, Flex, Typography, Popover, Input } from "antd";
import { format } from "date-fns";
import { prepareCSVString } from "utils";

export const AdBlockReview = () => {
  const [adblocks, setAdblocks] = useState([]);
  const [selectedRow, setSelectedRow] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const { userInfo } = useAuthContext();

  const handleClick = (data, flag, action) => {
    if (flag) {
      window.open(`/${flag}/${data.circular_uuid}/${data.page_uuid}/${data.adblock_uuid}`, '_blank');
      return;
    } else if (action === 'block_ok') {
      setIsLoading(true);
      if (data) setSelectedRow(data);
      markBlockReviewed(data.id)
        .then((res) => {
          if (res.success) {
            setIsLoading(false);
            setAdblocks(adblocks.filter((item) => item.id !== data.id));
            setTotalCount(totalCount - 1);
          } else {
            // Handle error
            console.error("Error marking block as reviewed", res.error);
          }
        })
        .catch((error) => {
          setIsLoading(false);
          console.error("Error marking block as reviewed", error);
        });
    }
    else if (action === 'take_notes') {
      if (data) {
        setSelectedRow(data);
      }
      handleOpenChange(true, data.id);
    }
  };

  const columns = [
    {
      title: 'Retailer', dataIndex: 'retailer_name', key: 'retailer_name',
    },
    {
      title: 'Parent Ad ID', dataIndex: 'parent_adblock_id', key: 'parent_adblock_id'
    },
    { title: 'Requested By', dataIndex: 'created_by_username', key: 'created_by_username' },
    {
      title: 'Requested On', dataIndex: 'created_at', key: 'created_at',
      render: (date) => (date ? format(new Date(date), "MM/dd/yyyy hh:mm a") : "-"),
      sorter: (a, b) => a.created_at - b.created_at
    },
    {
      title: 'Actions', key: 'action', align: 'center',
      render: (_, record) => (
        <>
          <Button variant="link" color="cyan" onClick={() => handleClick(record, 'product')}>Product</Button>
          <Button variant="link" color="cyan" className="mx-1" onClick={() => handleClick(record, 'offer')}>Offer</Button>
          <Button color="primary" variant="solid" className="mx-2" onClick={() => handleClick(record, null, 'block_ok')}>Block OK</Button>
          <Popover
            title="Add Review Notes"
            trigger='click'
            content={
              <>
                <Input.TextArea
                  rows={4}
                  placeholder="Add your review notes here..."
                  value={selectedRow?.review_note || ''}
                  onChange={(e) => setSelectedRow({ ...selectedRow, review_note: e.target.value })}
                />
                <Button disabled={isLoading} type="primary" onClick={() => saveReviewNote()}>Save Notes</Button>
              </>
            }
            open={selectedRow?.id === record.id}
            onOpenChange={(visible) => handleOpenChange(visible, record.id)}
          >
            <Button color="danger" variant="outlined" onClick={() => handleClick(record, null, 'take_notes')}>Corrected</Button>
          </Popover>

        </>
      )
    },
  ];

  const init = async () => {
    setIsLoading(true);
    const res = await getBlockToReview();
    if (res.success) {
      setIsLoading(false);
      setAdblocks(res.payload);
      setTotalCount(res.payload.length);
    } else {
      //Raise Error
    }
  };

  useEffect(() => {
    init();
    return () => { };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportAdBlocks = () => {
    const headerMappings = {
      'Retailer': 'retailer_name', 'Parent Ad ID': 'parent_adblock_id',
      'Requested On': 'created_at', 'Requested By': 'created_by_username'
    };
    const exportAdBlocks = adblocks.map((adblock) => {
      return {
        ...adblock,
        created_at: (adblock.created_at ? format(new Date(adblock.created_at), "MM/dd/yyyy hh:mm a") : "-")
      };
    });
    const url = window.URL.createObjectURL(
      new Blob([prepareCSVString(exportAdBlocks, { headerMappings })], { type: "text/plain" })
    );
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = `ReviewAdblocks-${new Date().toLocaleDateString().replaceAll('/', '-')}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const saveReviewNote = () => {
    setIsLoading(true);
    markBlockReviewed(selectedRow.id, selectedRow.review_note)
      .then((res) => {
        if (res.success) {
          setAdblocks(adblocks.filter((item) => item.id !== selectedRow.id));
          setTotalCount(totalCount - 1);
          setSelectedRow(null);
          setIsLoading(false);
        } else {
          console.error("Error marking block as reviewed", res.error);
        }
      })
      .catch((error) => {
        console.error("Error marking block as reviewed", error);
        setSelectedRow(null);
        setIsLoading(false);
      });

  };

  const handleOpenChange = (visible, rowId) => {
    if (visible && selectedRow?.id !== rowId) {
      const record = adblocks.find(item => item.id === rowId);
      setSelectedRow(record || null);
    }
    else {
      setSelectedRow(null);
    }
  };

  return (
    <MainLayout>
      <TableSection>
        <Flex vertical>
          <Table bordered loading={isLoading} size="medium" tableLayout="auto" style={{ 'marginRight': '0.2%' }}
            dataSource={adblocks} columns={columns} scroll={{ y: 620 }}
            pagination={{ defaultPageSize: 50 }}
            title={() => (
              <Flex className="" align="end" justify="space-between">
                <div>
                  <Typography.Title level={4}> Review AdBlocks ({adblocks.length} / {totalCount})</Typography.Title>
                </div><div>
                  {(userInfo?.user.is_moderator || userInfo?.user.is_superuser) &&
                    <Button className="ml-2" type="primary" onClick={exportAdBlocks}>Export</Button>
                  }</div>
              </Flex>
            )}
          ></Table>
        </Flex>
      </TableSection>
    </MainLayout>
  );
};

const TableSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;
